import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, Space, Tooltip } from 'antd';
import { FullscreenOutlined, ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import ReportChart, { ReportType as ChartReportType } from './chart/ReportChart';
import { ReportTable } from './report-table/ReportTable';
import { ReportType as TableReportType } from './report-table/types';
import { ReportData } from '../../hooks/useReportLoader';
import { useReportDataProvider, ReportDataProviderConfig } from '../../hooks/useReportDataProvider';
import { useActions } from '../../hooks/useActions';

/**
 * Props для компонента FullReportModal.
 */
export interface FullReportModalProps {
    /** Видимость модального окна */
    visible: boolean;
    /** Callback для закрытия модального окна */
    onClose: () => void;
    /** Заголовок модального окна */
    title?: string;
    /** ID блока дашборда */
    blockId?: string;
    /** Конфигурация отчёта */
    config?: ReportDataProviderConfig;
    /** Тип отчёта */
    reportType?: ChartReportType;
    /** Дата блока дашборда (формат: "type_start_end") */
    date?: string;
    /** Показывать график */
    showChart?: boolean;
    /** Показывать таблицу */
    showTable?: boolean;
    /** Тип графика (для отображения) */
    chartType?: string;
}

/**
 * Преобразует тип отчёта из ChartReportType в TableReportType.
 */
const chartToTableReportType = (chartType: ChartReportType): TableReportType => {
    switch (chartType) {
        case 'registration':
            return 'universal';
        case 'order':
            return 'order';
        case 'payment':
            return 'payment';
        case 'ad':
            return 'ad';
        case 'webinar':
            return 'webinar';
        case 'visit':
            return 'visit';
        default:
            return 'universal';
    }
};

/**
 * Определяет тип отчёта из даты блока дашборда.
 */
const getReportTypeFromDate = (date?: string): ChartReportType => {
    if (!date) return 'registration';
    
    const parts = date.split('_');
    const type = parts[0];
    
    switch (type) {
        case 'registration':
        case 'universal':
            return 'registration';
        case 'ad':
            return 'ad';
        case 'order':
            return 'order';
        case 'payment':
            return 'payment';
        case 'webinar':
            return 'webinar';
        case 'visit':
            return 'visit';
        default:
            return 'registration';
    }
};

/**
 * Получает URL страницы отчёта по типу.
 */
const getReportPagePath = (reportType: ChartReportType): string => {
    switch (reportType) {
        case 'ad':
            return '/ad-reports';
        case 'order':
            return '/order-reports';
        case 'payment':
            return '/payment-reports';
        case 'webinar':
            return '/webinar-reports';
        case 'visit':
            return '/reports/visits';
        case 'registration':
        default:
            return '/reports/registrations';
    }
};

/**
 * Модальное окно для просмотра полного отчёта из дашборда.
 * 
 * Отображает ReportChart и ReportTable в режиме displayMode="full",
 * позволяя просматривать полные данные отчёта без перехода на страницу.
 * 
 * @example
 * <FullReportModal
 *   visible={isModalVisible}
 *   onClose={() => setIsModalVisible(false)}
 *   title="Отчёт по регистрациям"
 *   blockId="block-123"
 *   reportType="registration"
 *   showChart={true}
 *   showTable={true}
 * />
 */
export const FullReportModal: FC<FullReportModalProps> = ({
    visible,
    onClose,
    title = 'Полный отчёт',
    blockId,
    config,
    reportType: propReportType,
    date,
    showChart = true,
    showTable = true,
    chartType,
}) => {
    const navigate = useNavigate();
    const { setMetaTableFetching, setMetaChartFetching } = useActions();
    
    // Определяем тип отчёта
    const reportType = useMemo(() => {
        if (propReportType) return propReportType;
        return getReportTypeFromDate(date);
    }, [propReportType, date]);
    
    // Тип отчёта для таблицы
    const tableReportType = useMemo(() => chartToTableReportType(reportType), [reportType]);
    
    // Используем хук для получения данных отчёта
    const {
        resolvedConfig,
        linkedReportState,
        isLinkedReport,
        getReportPageUrl,
    } = useReportDataProvider({
        config: config || {},
        context: 'modal',
        blockId,
        reportType,
        date,
    });
    
    // Ref для отслеживания, была ли уже инициирована загрузка
    const dataFetchInitiatedRef = useRef(false);
    
    // При открытии модального окна инициируем загрузку данных для блока
    useEffect(() => {
        if (visible && blockId && !dataFetchInitiatedRef.current) {
            dataFetchInitiatedRef.current = true;
            // Инициируем загрузку данных таблицы и графика для блока
            if (showTable) {
                setMetaTableFetching({ value: true, blockId });
            }
            if (showChart) {
                setMetaChartFetching({ value: true, blockId });
            }
        }
        
        // Сбрасываем флаг при закрытии модального окна
        if (!visible) {
            dataFetchInitiatedRef.current = false;
        }
    }, [visible, blockId, showTable, showChart, setMetaTableFetching, setMetaChartFetching]);
    
    /**
     * Переход на страницу отчёта.
     */
    const handleNavigateToReport = useCallback(() => {
        const url = getReportPageUrl();
        onClose();
        navigate(url);
    }, [getReportPageUrl, onClose, navigate]);
    
    /**
     * Открыть в новой вкладке.
     */
    const handleOpenInNewTab = useCallback(() => {
        const url = getReportPageUrl();
        window.open(url, '_blank');
    }, [getReportPageUrl]);
    
    // Заголовок модального окна
    const modalTitle = useMemo(() => {
        if (isLinkedReport && resolvedConfig?.values?.name) {
            return resolvedConfig.values.name;
        }
        return title;
    }, [isLinkedReport, resolvedConfig, title]);
    
    // Footer модального окна
    const modalFooter = useMemo(() => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
                <Tooltip title="Открыть на странице отчёта">
                    <Button
                        data-help-id="full-report-open"
                        icon={<FullscreenOutlined />}
                        onClick={handleNavigateToReport}
                    >
                        Открыть в отчёте
                    </Button>
                </Tooltip>
                <Tooltip title="Открыть в новой вкладке">
                    <Button
                        data-help-id="full-report-new-tab"
                        icon={<ExportOutlined />}
                        onClick={handleOpenInNewTab}
                    >
                        В новой вкладке
                    </Button>
                </Tooltip>
            </Space>
            <Button data-help-id="full-report-close" onClick={onClose}>
                Закрыть
            </Button>
        </div>
    ), [handleNavigateToReport, handleOpenInNewTab, onClose]);

    return (
        <Modal
            title={modalTitle}
            visible={visible}
            onCancel={onClose}
            footer={modalFooter}
            width="95vw"
            style={{ top: 20 }}
            bodyStyle={{ 
                maxHeight: 'calc(100vh - 200px)', 
                overflow: 'auto',
                padding: '16px 24px',
            }}
            destroyOnClose
        >
            {linkedReportState.loading ? (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: 300 
                }}>
                    <span>Загрузка отчёта...</span>
                </div>
            ) : linkedReportState.error ? (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: 300,
                    color: '#ff4d4f',
                }}>
                    <span>Ошибка загрузки отчёта: {linkedReportState.error.message}</span>
                </div>
            ) : (
                <>
                    {showChart && (
                        <div style={{ marginBottom: 24 }}>
                            <ReportChart
                                reportType={reportType}
                                blockId={blockId}
                                displayMode="full"
                                isModal={true}
                                date={date}
                            />
                        </div>
                    )}
                    
                    {showTable && (
                        <div>
                            <ReportTable
                                reportType={tableReportType}
                                blockId={blockId}
                                displayMode="full"
                                isModal={true}
                                date={date}
                            />
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default FullReportModal;
